import * as THREE from 'three';
import { getUnitBoxGeometry } from '../../../../utils/threeResourcePool';

const cache = new Map<string, THREE.BufferGeometry>();

export function getStockerUnitBox(): THREE.BoxGeometry {
  return getUnitBoxGeometry();
}

export function getStockerRoundedColumn(radius: number, height: number, segments = 12): THREE.CylinderGeometry {
  const key = `col-${radius.toFixed(4)}-${height.toFixed(4)}-${segments}`;
  let g = cache.get(key);
  if (!g) {
    g = new THREE.CylinderGeometry(radius, radius * 1.02, height, segments);
    cache.set(key, g);
  }
  return g as THREE.CylinderGeometry;
}

export function getStockerTrayDeck(thickness: number): THREE.BoxGeometry {
  const key = `tray-${thickness.toFixed(4)}`;
  let g = cache.get(key);
  if (!g) {
    g = new THREE.BoxGeometry(1, thickness, 1);
    cache.set(key, g);
  }
  return g as THREE.BoxGeometry;
}

export type StockerTrayBatchInstance = {
  position: [number, number, number];
  scale: [number, number, number];
  rotation?: [number, number, number];
};

function resolveStockerTierPitch(
  slots: ReadonlyArray<{ position: [number, number, number] }>,
  slotH?: number,
  tierGap?: number
): number {
  if (slotH != null && tierGap != null) {
    return slotH + tierGap;
  }
  const ys = [...new Set(slots.map((s) => Math.round(s.position[1] * 1000) / 1000))].sort(
    (a, b) => a - b
  );
  if (ys.length >= 2) {
    return ys[1]! - ys[0]!;
  }
  return 0.55;
}

/** Build instanced tray deck + lip segments for a rack column. */
export function buildStockerTrayInstances(
  slots: ReadonlyArray<{ position: [number, number, number] }>,
  trayW: number,
  trayD: number,
  trayH: number,
  slotH?: number,
  tierGap?: number
): StockerTrayBatchInstance[] {
  const out: StockerTrayBatchInstance[] = [];
  const tierPitch = resolveStockerTierPitch(slots, slotH, tierGap);
  const lipH = Math.min(trayH * 1.35, tierPitch * 0.36);
  for (const slot of slots) {
    const [x, y, z] = slot.position;
    const lipCenterY = y + trayH * 0.5 + lipH * 0.5;
    out.push({ position: [x, y, z], scale: [trayW, trayH, trayD] });
    out.push({
      position: [x - trayW * 0.42, lipCenterY, z],
      scale: [trayW * 0.06, lipH, trayD * 0.92],
    });
    out.push({
      position: [x, lipCenterY, z - trayD * 0.42],
      scale: [trayW * 0.88, lipH, trayD * 0.06],
    });
  }
  return out;
}
